import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';

// Base path for the page's static assets, copied from the v0 source project.
export const IMG = '/img/inshurlp';

// The 11 photo assets below already ship with the /turo-alternative port as
// .webp; reuse them instead of duplicating the v0 PNGs (see commit 199b2c7).
export const SHARED_IMG = '/img/turo-alternative';

export const BRAND_GREEN = '#33F7BE';
export const BRAND_GREEN_HOVER = '#2be0a8';
export const INSHUR_YELLOW = '#F6BD2C';

// CTA destinations are ported verbatim from the v0 source. Several of these
// routes do not exist on rentid.io yet — they are intentionally left as-is.
export const GET_STARTED_URL = 'https://app.rentid.io/sign-up';
export const BOOK_DEMO_URL = 'https://api.leadconnectorhq.com/widget/form/xSNa1tu0CCXyei8UJ2ew';

/** True for absolute URLs pointing off-site, which open in a new tab. */
const isExternal = (href = '') => /^https?:\/\//i.test(href);

/** Pill CTA — mint by default, outlined with tone="dark". */
export const Cta = ({ href, label, tone = 'light', className = '' }) => (
  <a
    href={href}
    {...(isExternal(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className={`ilp-cta${tone === 'dark' ? ' ilp-cta--dark' : ''}${className ? ` ${className}` : ''}`}
  >
    {label}
  </a>
);

/** "Get Started" CTA with the sweeping shimmer overlay and arrow. */
export const ShimmerCta = ({ href = 'https://app.rentid.io/sign-up', label = 'Get Started' }) => (
  <a
    href={href}
    {...(isExternal(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className="ilp-shimmer-cta"
  >
    <span aria-hidden="true" className="ilp-shimmer-sweep" />
    <span className="ilp-shimmer-label">{label}</span>
    <ArrowRight className="ilp-shimmer-arrow" aria-hidden="true" />
  </a>
);

/** Four-point star separator used between hero microcopy items. */
export const Sparkle = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="ilp-sparkle">
    <path d="M12 0c.6 6.1 5.9 11.4 12 12-6.1.6-11.4 5.9-12 12-.6-6.1-5.9-11.4-12-12C6.1 11.4 11.4 6.1 12 0Z" />
  </svg>
);

/**
 * Corner-splay "maximize" glyph shown on hover over an expandable card.
 * Shared by the roadside, audience and website/guest cards.
 */
export const MaximizeGlyph = ({ variant = 'light' }) => (
  <span className={`ilp-maximize ilp-maximize--${variant}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline className="ilp-maximize-tl" points="9 4 4 4 4 9" />
      <polyline className="ilp-maximize-br" points="15 20 20 20 20 15" />
    </svg>
  </span>
);

/** Reveal-on-scroll: returns a ref and whether the element has entered view. */
export const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (
      typeof IntersectionObserver === 'undefined' ||
      (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, shown };
};

/** True once the element has been scrolled into view; drives timed animations. */
export const useInView = (threshold = 0.35) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => setInView(entries[0].isIntersecting), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, inView };
};
